// Chakra imports
import {
  Box,
  Button,
  Flex,
  Modal,        // Import Modal dari Chakra UI
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure, 
  Text,
  Grid,
} from "@chakra-ui/react";
// Custom components
import Card from "components/card/Card.js";
import React, { useRef, useState, useEffect } from "react";
import Swal from 'sweetalert2'
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'jspdf-autotable';
import { useTranslation } from 'react-i18next';

export default function Upload(props) {
  const { t } = useTranslation();
  const { used, total, ...rest } = props;
  const canvasRef = useRef(null);
  const containerRef = useRef(null);


  const [zoomLevel, setZoomLevel] = useState(1); // State untuk menyimpan level zoom
  const [radiographImage, setRadiographImage] = useState(null); // State untuk menyimpan gambar
  const [c2Points, setC2Points] = useState([]); // State untuk C2 points
  const [c3Points, setC3Points] = useState([]); // State untuk C3 points
  const [c4Points, setC4Points] = useState([]); // State untuk C4 points
  // ======================================================
  // CALIBRATION & LANDMARKING WORKFLOW
  // ======================================================

  const [calibrationPoints, setCalibrationPoints] = useState([]);

  const [landmarkStage, setLandmarkStage] = useState("idle");

  const [landmarkingComplete, setLandmarkingComplete] = useState(false);

  // Jarak aktual dua titik pada penggaris radiograf
  // Sesuaikan jika jarak calibration Anda bukan 10 mm
  const CALIBRATION_DISTANCE_MM = 10;

  const [calibrationPointIndex, setCalibrationPointIndex] = useState(0);
  const [c2Index, setC2Index] = useState(0);
  const [c3Index, setC3Index] = useState(0);
  const [c4Index, setC4Index] = useState(0);

// Gunakan ref supaya pergerakan real-time
const offsetXRef = useRef(0);
const offsetYRef = useRef(0);
const lastMouseXRef = useRef(0);
const lastMouseYRef = useRef(0);
const isDraggingRef = useRef(false);

const [offsetX, setOffsetX] = useState(0);
const [offsetY, setOffsetY] = useState(0);

const imageRef = useRef(null); // referensi untuk gambar
  const zoomRef = useRef(null);  // referensi untuk kontrol zoom (opsional)

  const [zoom, setZoom] = useState(1);

    // State untuk undo/redo
    const [undoStack, setUndoStack] = useState([]);
    const [redoStack, setRedoStack] = useState([]);
    const [canvasDimensions, setCanvasDimensions] = useState({ width: 670, height: 600 });
    const { isOpen, onOpen, onClose } = useDisclosure();  // State untuk modal
    const { isOpen: isTutorialOpen, onOpen: onTutorialOpen, onClose: onTutorialClose } = useDisclosure();
    const [analysisResults, setAnalysisResults] = useState({
      angleC2: null,
      ConcvC2: null,
      angleC3: null,
      ConcvC3: null,
      angleC4: null,
      ConcvC4: null,
      BARC3: null,
      BARC4: null,
      PARC3: null,
      PARC4: null,
      MorfologiC3: null,
      MorfologiC4: null,
      CS: null,
      CSDescription: null,
      PointC2: null,
      PointC3: null,
      PointC4: null,
    });

  useEffect(() => {
    if (radiographImage) {
      drawImageWithZoomAndDrawing(radiographImage, zoomLevel, c2Points, c3Points, c4Points, calibrationPoints);
    }
  }, [c2Points, c3Points, c4Points, zoomLevel, radiographImage, calibrationPoints]);

    // Function to handle resizing the canvas responsively
    useEffect(() => {
      const updateCanvasSize = () => {
        if (containerRef.current) {
          const containerWidth = containerRef.current.offsetWidth;
          const containerHeight = containerWidth * (600 / 670); // Maintain aspect ratio
          setCanvasDimensions({ width: containerWidth, height: containerHeight });
        }
      };
  
      // Initial sizing
      updateCanvasSize();
  
      // Update canvas size when window is resized
      window.addEventListener("resize", updateCanvasSize);
      return () => window.removeEventListener("resize", updateCanvasSize);
    }, []);

    const handleReset = () => {
        // Reset gambar radiograf
        setRadiographImage(null);

        // Reset calibration
        setCalibrationPoints([]);
        setCalibrationPointIndex(0);

        // Reset cervical landmarks
        setC2Points([]);
        setC3Points([]);
        setC4Points([]);
        setC2Index(0);
        setC3Index(0);
        setC4Index(0);

        // Reset workflow
        setLandmarkStage("idle");
        setLandmarkingComplete(false);

        // Reset undo / redo
        setUndoStack([]);
        setRedoStack([]);

        // Reset ZOOM ke level awal (1x, tanpa perbesaran/perkecilan)
        setZoomLevel(1);
        zoomRef.current = 1;   // <-- ikut disinkronkan, supaya drag pakai nilai yang benar

        // Reset posisi geser gambar (pan/offset)
        setOffsetX(0);
        setOffsetY(0);
        offsetXRef.current = 0;
        offsetYRef.current = 0;

        // Bersihkan canvas secara manual
        const canvas = canvasRef.current;
        if (canvas) {
          const ctx = canvas.getContext('2d');
          ctx.clearRect(0, 0, canvas.width, canvas.height);
        }

        // Reset analysis
        setAnalysisResults({
          AngleC2: null,
          ConcvC2: null,
          AngleC3: null,
          ConcvC3: null,
          AngleC4: null,
          ConcvC4: null,
          BARC3: null,
          BARC4: null,
          PARC3: null,
          PARC4: null,
          MorfologiC3: null,
          MorfologiC4: null,
          CS: null,
          CSDescription: null,
          PointC2: null,
          PointC3: null,
          PointC4: null,
          C2ConcDepth: null,
          C3ConcDepth: null,
          C4ConcDepth: null,
          CalibrationMmPerPixel: null
        });
    }
  

  const handleOpenButtonClick = () => {
    handleReset();
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';

    input.onchange = (event) => {
      const file = event.target.files[0];
      const reader = new FileReader();

      reader.onload = () => {
          const img = new Image();
          img.onload = () => {

            setRadiographImage(img);

            // Reset workflow
            setCalibrationPoints([]);
            setC2Points([]);
            setC3Points([]);
            setC4Points([]);

            setCalibrationPointIndex(0);
            setC2Index(0);
            setC3Index(0);
            setC4Index(0);

            setLandmarkingComplete(false);

            // Mulai dari calibration
            setLandmarkStage("calibration");

            drawImageWithZoomAndDrawing(
              img,
              zoomLevel
            );

            // Alert pertama
            Swal.fire({
              icon: "info",
              title: t('swal.calibrationTitle'),
              html: `
                <div style="text-align:left">
                  <p>
                    ${t('swal.calibrationBody')}
                  </p>

                  <p>
                    ${t('swal.calibrationBody2')}
                  </p>

                  <p>
                    ${t('swal.calibrationBody3')}
                  </p>
                </div>
              `,
              confirmButtonText: t('swal.calibrationStart'),
              allowOutsideClick: false
            });

          };

          img.src = reader.result;
      };

      reader.readAsDataURL(file);
    };

    input.click();
  };

// Modifikasi drawImageWithZoomAndDrawing untuk pakai offset dari ref
const drawImageWithZoomAndDrawing = (image, zoom) => {
  const canvas = canvasRef.current;
  const ctx = canvas.getContext('2d');
  ctx.clearRect(0, 0, canvas.width, canvas.height);

  const aspectRatio = image.width / image.height;
  let drawWidth = canvas.width * zoom;
  let drawHeight = canvas.height * zoom;

  if (canvas.width / canvas.height > aspectRatio) {
    drawWidth = canvas.height * aspectRatio * zoom;
  } else {
    drawHeight = canvas.width / aspectRatio * zoom;
  }

  const xOffset = (canvas.width - drawWidth) / 2 + offsetXRef.current;
  const yOffset = (canvas.height - drawHeight) / 2 + offsetYRef.current;

  ctx.drawImage(image, xOffset, yOffset, drawWidth, drawHeight);
  drawPointsAndLines(ctx);
};

// Event handler untuk drag real-time
const handleMouseDown = (e) => {
  isDraggingRef.current = true;
  lastMouseXRef.current = e.clientX;
  lastMouseYRef.current = e.clientY;
};

const handleMouseMove = (e) => {
  if (!isDraggingRef.current) return;
  const dx = e.clientX - lastMouseXRef.current;
  const dy = e.clientY - lastMouseYRef.current;

  offsetXRef.current += dx;
  offsetYRef.current += dy;

  lastMouseXRef.current = e.clientX;
  lastMouseYRef.current = e.clientY;

  // Update state untuk sinkronisasi (opsional, kalau mau UI lain ikut berubah)
  setOffsetX(offsetXRef.current);
  setOffsetY(offsetYRef.current);

  // Gambar ulang langsung biar smooth
  if (imageRef.current) {
    drawImageWithZoomAndDrawing(imageRef.current, zoomRef.current);
  }
};

const handleMouseUp = () => {
  isDraggingRef.current = false;
};

// Pasang event listener ke canvas
useEffect(() => {
  const canvas = canvasRef.current;
  canvas.addEventListener('mousedown', handleMouseDown);
  canvas.addEventListener('mousemove', handleMouseMove);
  canvas.addEventListener('mouseup', handleMouseUp);
  canvas.addEventListener('mouseleave', handleMouseUp);

  return () => {
    canvas.removeEventListener('mousedown', handleMouseDown);
    canvas.removeEventListener('mousemove', handleMouseMove);
    canvas.removeEventListener('mouseup', handleMouseUp);
    canvas.removeEventListener('mouseleave', handleMouseUp);
  };
}, []);

  const drawPointsAndLines = (ctx) => {
    ctx.strokeStyle = 'blue';
    ctx.fillStyle = 'red';
    ctx.lineWidth = 1;

    const drawCircle = (point) => {
      ctx.beginPath();
      ctx.arc(point.x, point.y, 2, 0, Math.PI * 2);
      ctx.fill();
    };

    const drawLine = (point1, point2) => {
      ctx.beginPath();
      ctx.moveTo(point1.x, point1.y);
      ctx.lineTo(point2.x, point2.y);
      ctx.stroke();
    };

    for (let i = 0; i < calibrationPointIndex; i++) {
      drawCircle(calibrationPoints[i]);
      if (i > 0) {
        drawLine(calibrationPoints[i - 1], calibrationPoints[i]);
      }
    }

    for (let i = 0; i < c2Index; i++) {
      drawCircle(c2Points[i]);
      if (i > 0) {
        drawLine(c2Points[i - 1], c2Points[i]);
      }
    }

    for (let i = 0; i < c3Index; i++) {
      drawCircle(c3Points[i]);
      if (i > 0) {
        drawLine(c3Points[i - 1], c3Points[i]);
      }
    }

    for (let i = 0; i < c4Index; i++) {
      drawCircle(c4Points[i]);
      if (i > 0) {
        drawLine(c4Points[i - 1], c4Points[i]);
      }
    }
  };
  const pushToUndoStack = () => {
  setUndoStack(prev => [
    ...prev,
    {
      calibrationPoints,
      c2Points,
      c3Points,
      c4Points,
      calibrationPointIndex,
      c2Index,
      c3Index,
      c4Index,
      landmarkStage,
    }
  ]);
  setRedoStack([]); // aksi baru menghapus histori redo
};
  
  const handleCanvasClick = (event) => {
    const canvas = canvasRef.current;

  if (!canvas) return;

  // Jangan menerima titik jika belum ada tahap aktif
  if (landmarkStage === "idle") {
    return;
  }

  // Jangan menerima titik setelah selesai
  if (landmarkStage === "complete") {
    return;
  }

  const rect = canvas.getBoundingClientRect();

  const scaleX =
    canvas.width / rect.width;

  const scaleY =
    canvas.height / rect.height;

  const x =
    (event.clientX - rect.left) * scaleX;

  const y =
    (event.clientY - rect.top) * scaleY;

  const point = {
    x,
    y
  };


  // ==================================================
  // STEP 1 — CALIBRATION
  // ==================================================

  if (landmarkStage === "calibration") {

    if (calibrationPoints.length >= 2) {
      return;
    }
pushToUndoStack();  
    const newPoints = [
      ...calibrationPoints,
      point
    ];

    setCalibrationPoints(newPoints);

    setCalibrationPointIndex(newPoints.length);

    // Setelah 2 titik calibration
    if (newPoints.length === 2) {

      const calibrationPixelDistance =
        calculateDistance(
          newPoints[0],
          newPoints[1]
        );

      const mmPerPixel =
        CALIBRATION_DISTANCE_MM /
        calibrationPixelDistance;

      setAnalysisResults(prev => ({
        ...prev,
        CalibrationMmPerPixel:
          mmPerPixel
      }));

      setLandmarkStage("c2");

      Swal.fire({
        icon: "success",
        title: t('swal.calibrationDone'),
        html: `
          <p>
            ${t('swal.calibrationDoneBody')}
          </p>

          <p>
            ${t('swal.step2C2')}
          </p>

          <p>
            ${t('swal.markC2')}
          </p>

          <ul style="text-align:left">
            <li>${t('swal.pointP1')}</li>
            <li>${t('swal.pointP2')}</li>
            <li>${t('swal.pointP3')}</li>
          </ul>
        `,
        confirmButtonText: t('swal.c2Start'),
        allowOutsideClick: false
      });
    }

    return;
  }


  // ==================================================
  // STEP 2 — C2
  // ==================================================

  if (landmarkStage === "c2") {

    if (c2Points.length >= 3) {
      return;
    }
pushToUndoStack();  
    const newPoints = [
      ...c2Points,
      point
    ];

    setC2Points(newPoints);

    setC2Index(newPoints.length);

    // Setelah 3 titik C2
    if (newPoints.length === 3) {

      setLandmarkStage("c3");

      Swal.fire({
        icon: "success",
        title: t('swal.c2Done'),
        html: `
          <p>
            ${t('swal.c2DoneBody')}
          </p>

          <p>
            ${t('swal.step3C3')}
          </p>

          <p>
            ${t('swal.markC3')}
          </p>

          <ul style="text-align:left">
            <li>${t('swal.pointP1')}</li>
            <li>${t('swal.pointP2')}</li>
            <li>${t('swal.pointP3')}</li>
            <li>${t('swal.pointP4')}</li>
            <li>${t('swal.pointP5')}</li>
          </ul>
        `,
        confirmButtonText: t('swal.c3Start'),
        allowOutsideClick: false
      });
    }

    return;
  }


  // ==================================================
  // STEP 3 — C3
  // ==================================================

  if (landmarkStage === "c3") {

    if (c3Points.length >= 5) {
      return;
    }
pushToUndoStack();  
    const newPoints = [
      ...c3Points,
      point
    ];

    setC3Points(newPoints);

    setC3Index(newPoints.length);

    // Setelah 5 titik C3
    if (newPoints.length === 5) {

      setLandmarkStage("c4");

      Swal.fire({
        icon: "success",
        title: t('swal.c3Done'),
        html: `
          <p>
            ${t('swal.c3DoneBody')}
          </p>

          <p>
            ${t('swal.step4C4')}
          </p>

          <p>
            ${t('swal.markC4')}
          </p>

          <ul style="text-align:left">
            <li>${t('swal.pointP1')}</li>
            <li>${t('swal.pointP2')}</li>
            <li>${t('swal.pointP3')}</li>
            <li>${t('swal.pointP4')}</li>
            <li>${t('swal.pointP5')}</li>
          </ul>
        `,
        confirmButtonText: t('swal.c4Start'),
        allowOutsideClick: false
      });
    }

    return;
  }


  // ==================================================
  // STEP 4 — C4
  // ==================================================

  if (landmarkStage === "c4") {

    if (c4Points.length >= 5) {
      return;
    }
pushToUndoStack();  
    const newPoints = [
      ...c4Points,
      point
    ];

    setC4Points(newPoints);

    setC4Index(newPoints.length);

    // Setelah 5 titik C4
    if (newPoints.length === 5) {

      setLandmarkStage("complete");

      setLandmarkingComplete(true);

      Swal.fire({
        icon: "success",
        title: t('swal.c4Done'),
        html: `
          <p>
            ${t('swal.c4DoneBody')}
          </p>

          <hr>

          <p>
            ${t('swal.summaryCalibration')}: <b>2/2</b>
          </p>

          <p>
            C2: <b>3/3</b>
          </p>

          <p>
            C3: <b>5/5</b>
          </p>

          <p>
            C4: <b>5/5</b>
          </p>

          <hr>

          <p>
            ${t('swal.totalPoints')}
          </p>

          <p>
            ${t('swal.analyzeButton')}
          </p>
        `,
        confirmButtonText: "OK",
        allowOutsideClick: false
      });
    }

    return;
  }
  };

let angleC2,
angleC3,
angleC4,
PARC3,
BARC3,
BARC4,
MorfologiC3,
MorfologiC4,
morfologiC3Key,
morfologiC4Key,
pointC2,
pointC3,
pointC4;

const analisis = async () => {
  // Validasi kelengkapan data SEBELUM memproses apapun
  if (
    calibrationPoints.length < 2 ||
    c2Points.length < 3 ||
    c3Points.length < 5 ||
    c4Points.length < 5
  ) {
    Swal.fire({
      icon: "warning",
      title: t('swal.landmarkIncomplete'),
      text: t('swal.landmarkIncompleteText'),
    });
    return; // hentikan di sini, jangan lanjut ke kalkulasi
  }
  try {
    // Eksekusi calculateC2Measurements, calculateC3Measurements, dan calculateC4Measurements secara berurutan
    calculateC2Measurements();
    calculateC3Measurements();
    calculateC4Measurements();

    // Setelah semua selesai, jalankan calculateCS
    calculateCS();
    

    Swal.fire({
      position: "top-end",
      icon: "success",
      title: t('swal.analysisDone'),
      showConfirmButton: true,
      confirmButtonText: t('swal.viewResult')
    }).then((result) => {
      if (result.isConfirmed) {
        onOpen();
      }
    });
  } catch (error) {
    console.error("Terjadi kesalahan dalam analisis:", error);
    Swal.fire({
      icon: "error",
      title: t('swal.errorTitle'),
      text: t('swal.errorText'),
    });
  }
};

// Perbaikan pada fungsi kalkulasi
const calculateC2Measurements = () => {
  if (c2Points.length < 3) {
    console.error("Kurang dari 3 titik untuk C2.");
    return;
  }
  const mmPerPixel =
    analysisResults.CalibrationMmPerPixel;

  if (!mmPerPixel) {
    console.error(
      "Calibration belum tersedia."
    );
    return;
  }
  console.log(c2Points[0], c2Points[1], c2Points[2]);
  angleC2 = calculateConcavityDepth(c2Points[0], c2Points[1], c2Points[2], mmPerPixel);
  const concavityC2 = ConcavityIdentifyC2(angleC2);
  console.log("C2 Measurements - Angle:", angleC2, "Concavity:", concavityC2);
  pointC2 = [c2Points[0], c2Points[1], c2Points[2]]

  setAnalysisResults((prevResults) => ({
    ...prevResults,
    AngleC2: angleC2,
    ConcvC2: concavityC2,
    PointC2: pointC2
  }));
};

const calculateC3Measurements = () => {
  if (c3Points.length < 5) {
    console.error("Kurang dari 5 titik untuk C3.");
    return;
  }
  const mmPerPixel =
  analysisResults.CalibrationMmPerPixel;
  angleC3 = calculateConcavityDepth(c3Points[0], c3Points[1], c3Points[2], mmPerPixel);
  const concavityC3 = ConcavityIdentifyC3(angleC3);
  BARC3 = BARIdentify(c3Points[0], c3Points[3], c3Points[2]);
  PARC3 = PARIdentify(c3Points[4], c3Points[0], c3Points[3], c3Points[2]);
  const widthC3 = calculateDistance(c3Points[0], c3Points[2]);
  const heightC3 = calculateDistance(c3Points[0], c3Points[4]);
  const heightC3a = calculateDistance(c3Points[2], c3Points[3]);
  const squareC3 = widthC3 / heightC3a;
  MorfologiC3 = MorfologiC3f(squareC3, BARC3, PARC3, heightC3, heightC3a);
  pointC3 = [c3Points[0], c3Points[1], c3Points[2]]

  setAnalysisResults((prevResults) => ({
    ...prevResults,
    AngleC3: angleC3,
    ConcvC3: concavityC3,
    BARC3: BARC3,
    PARC3: PARC3,
    MorfologiC3: MorfologiC3,
    PointC3: pointC3
  }));
};

const calculateC4Measurements = () => {
  if (c4Points.length < 5) {
    console.error("Kurang dari 5 titik untuk C4.");
    return;
  }
  const mmPerPixel =
  analysisResults.CalibrationMmPerPixel;
  angleC4 = calculateConcavityDepth(c4Points[0], c4Points[1], c4Points[2], mmPerPixel);
  const concavityC4 = ConcavityIdentifyC4(angleC4);
  console.log(angleC4,+"p1"+ c4Points[0],+"p2"+ c4Points[1], +"p3"+c4Points[2]);
  BARC4 = BARIdentify(c4Points[0], c4Points[3], c4Points[2]);
  const PARC4 = PARIdentify(c4Points[4], c4Points[0], c4Points[3], c4Points[2]);
  const widthC4 = calculateDistance(c4Points[0], c4Points[2]);
  const heightC4 = calculateDistance(c4Points[0], c4Points[4]);
  const heightC4a = calculateDistance(c4Points[2], c4Points[3]);
  const squareC4 = widthC4 / heightC4a;
  MorfologiC4 = MorfologiC4f(squareC4, BARC4, PARC4, heightC4, heightC4a);
  pointC4 = [c4Points[0], c4Points[1], c4Points[2]]

  setAnalysisResults((prevResults) => ({
    ...prevResults,
    AngleC4: angleC4,
    ConcvC4: concavityC4,
    BARC4: BARC4,
    PARC4: PARC4,
    MorfologiC4: MorfologiC4,
    PointC4: pointC4
  }));
};

const calculateCS = () => {
  const C2ConcScore = angleC2 >= 0.60 && (pointC2[0].y > pointC2[1].y || pointC2[2].y > pointC2[1].y ) ? 1 : 0;
  const C3ConcScore = angleC3 >= 0.66 && (pointC3[0].y > pointC3[1].y || pointC3[2].y > pointC3[1].y ) ? 1 : 0;
  const C4ConcScore = ((angleC4 >= 0.69 && (pointC4[0].y > pointC4[1].y || pointC4[2].y > pointC4[1].y ))|| PARC3 <= 1 ? 1 : 0);
  const C3BARScore = BARC3 <= 1.39 ? 1 : 0;
  const C4BARScore = 
    BARC4 <= 1.19 &&
    (["verticalRect", "Persegi Panjang Vertikal"].includes(morfologiC3Key || MorfologiC3) &&
     ["verticalRect", "square", "Persegi Panjang Vertikal", "Persegi"].includes(morfologiC4Key || MorfologiC4))
      ? 1
      : 0;
  console.log(C2ConcScore, C3ConcScore , C4ConcScore ,C3BARScore ,C4BARScore, 1)
  const CS = C2ConcScore + C3ConcScore + C4ConcScore + C3BARScore + C4BARScore + 1;

  const descriptions = {
    1: t('csDescriptions.1'),
    2: t('csDescriptions.2'),
    3: t('csDescriptions.3'),
    4: t('csDescriptions.4'),
    5: t('csDescriptions.5'),
    6: t('csDescriptions.6')
  };

  const CSDescription = descriptions[CS] || t('swal.noDescription');

  setAnalysisResults((prevResults) => ({
    ...prevResults,
    CS: CS,
    CSDescription: CSDescription
  }));
};

  // ======================================================
  // CONCAVITY DEPTH
  // Depth of inferior vertebral concavity
  // ======================================================

  const calculateConcavityDepth = (
    p1,
    p2,
    p3,
    mmPerPixel
  ) => {

    // Panjang garis P1-P3
    const baseLength =
      calculateDistance(p1, p3);

    if (baseLength === 0) {
      return 0;
    }

    // Jarak tegak lurus P2 terhadap garis P1-P3
    const depthPixel =
      Math.abs(
        (p3.x - p1.x) * (p1.y - p2.y) -
        (p1.x - p2.x) * (p3.y - p1.y)
      ) /
      baseLength;

    // Konversi pixel → mm
    const depthMm =
      depthPixel * mmPerPixel;

    return depthMm;
  };

  const ConcavityIdentifyC2 = (
  depthMm
  ) => {

    // Toleransi numerik untuk membedakan
    // garis datar dari adanya cekungan.
    return depthMm > 0.60
      ? t('morphology.concave')
      : t('morphology.flat');
  };
    const ConcavityIdentifyC3 = (
    depthMm
    ) => {

      // Toleransi numerik untuk membedakan
      // garis datar dari adanya cekungan.
      return depthMm > 0.66
        ? t('morphology.concave')
        : t('morphology.flat');
    };
        const ConcavityIdentifyC4 = (
    depthMm
    ) => {

      // Toleransi numerik untuk membedakan
      // garis datar dari adanya cekungan.
      return depthMm > 0.69
        ? t('morphology.concave')
        : t('morphology.flat');
    };


  // Function to calculate the distance between two points
  const calculateDistance = (p1, p2) => {
    return Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
  };

  // Function to calculate BAR
  const BARIdentify = (lp, ua, la) => {
    return calculateDistance(lp, la) / calculateDistance(ua, la);
  };

  // Function to calculate PAR
  const PARIdentify = (up, lp, ua, la) => {
    return calculateDistance(up, lp) / calculateDistance(ua, la);
  };

  const MorfologiC3f = (square, bar, par, heightp, heighta) => {
    let key;
    if (bar >= 1.39 && par >= 1.37 && heightp > heighta) {
      key = "trapezoid";
    } else if (square <= 1.3 && square >= 0.9) {
      key = "square";
    } else {
      key = square < 1 ? "verticalRect" : "horizontalRect";
    }
    morfologiC3Key = key;
    return t(`morphology.${key}`);
  };

  const MorfologiC4f = (square, bar, par, heightp, heighta) => {
    let key;
    if (bar >= 1.19 && par >= 1.37 && heightp > heighta) {
      key = "trapezoid";
    } else if (square <= 1.3 && square >= 0.9) {
      key = "square";
    } else {
      key = square < 1 ? "verticalRect" : "horizontalRect";
    }
    morfologiC4Key = key;
    return t(`morphology.${key}`);
  };



  // Fungsi untuk memperbesar (zoom in)
  const handleZoomIn = () => {
    setZoomLevel((prevZoom) => {
      const newZoom = prevZoom + 0.1;
      if (radiographImage) {
        drawImageWithZoomAndDrawing(radiographImage, newZoom);
      }
      return newZoom;
    });
  };

  // Fungsi untuk memperkecil (zoom out)
  const handleZoomOut = () => {
    setZoomLevel((prevZoom) => {
      const newZoom = prevZoom - 0.1 > 0 ? prevZoom - 0.1 : 0.1; // Hindari zoom 0 atau negatif
      if (radiographImage) {
        drawImageWithZoomAndDrawing(radiographImage, newZoom);
      }
      return newZoom;
    });
  };

    // Snapshot kondisi saat ini (dipakai undo & redo)
const getSnapshot = () => ({
  calibrationPoints,
  c2Points,
  c3Points,
  c4Points,
  calibrationPointIndex,
  c2Index,
  c3Index,
  c4Index,
  landmarkStage,
});

// Terapkan snapshot ke state
const applySnapshot = (snap) => {
  setCalibrationPoints(snap.calibrationPoints);
  setC2Points(snap.c2Points);
  setC3Points(snap.c3Points);
  setC4Points(snap.c4Points);
  setCalibrationPointIndex(snap.calibrationPointIndex);
  setC2Index(snap.c2Index);
  setC3Index(snap.c3Index);
  setC4Index(snap.c4Index);

  // PENTING: kembalikan tahap landmark supaya bisa menitik ulang
  setLandmarkStage(snap.landmarkStage);
  setLandmarkingComplete(snap.landmarkStage === "complete");
};

// Fungsi undo
const handleUndo = () => {
  if (undoStack.length === 0) return;

  const lastState = undoStack[undoStack.length - 1];

  setUndoStack(undoStack.slice(0, -1));          // tidak memutasi state
  setRedoStack([...redoStack, getSnapshot()]);   // simpan kondisi sekarang
  applySnapshot(lastState);
};

// Fungsi redo
const handleRedo = () => {
  if (redoStack.length === 0) return;

  const nextState = redoStack[redoStack.length - 1];

  setRedoStack(redoStack.slice(0, -1));
  setUndoStack([...undoStack, getSnapshot()]);
  applySnapshot(nextState);
};

    const simpanData = () => {
      onClose();
      Swal.fire({
        title: t('swal.patientDataTitle'),
        html:
          `<input id="swal-input1" class="swal2-input" placeholder="${t('swal.codeInput')}">` +
          '</select>',
        focusConfirm: false,
        preConfirm: () => {
          const kode = document.getElementById('swal-input1').value;
    
          if (kode) {
            generatePDF(kode);
          } else {
            Swal.showValidationMessage(t('swal.validationError'));
          }
        }
      });
    };

    
    const generatePDF = async (kode) => {
      const doc = new jsPDF('p', 'mm', 'a4');
      const canvas = canvasRef.current;
      
      // Ambil gambar dari kanvas menggunakan html2canvas
      const canvasImage = await html2canvas(canvas, { scale: 3 });
      const imgData = canvasImage.toDataURL('image/png');
      
      // Mendapatkan tanggal saat ini untuk disimpan di PDF
      const today = new Date();
      const tanggalSimpan = today.toLocaleDateString('id-ID', {
        day: 'numeric', month: 'long', year: 'numeric'
      });

        // Hitung umur berdasarkan tanggal lahir
    
       // Mengambil gambar logo sebagai data URL
      const logoPath = process.env.PUBLIC_URL + '/logo.png'; // Path gambar logo
      const logoData = await fetch(logoPath)
        .then(res => res.blob())
        .then(blob => new Promise((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result);
          reader.readAsDataURL(blob);
        }));
    
      // Membuat layout PDF
      doc.setFontSize(14);
      doc.text(t('pdf.title'), 105, 15, { align: 'center' });
      
      // Tambahkan logo di bagian kiri atas
      const logoX = 10;
      const logoY = 20;
    
      // Header informasi pasien
      doc.autoTable({
        startY: logoY, // Adjust the startY to be below the logo
        body: [
          [{ content: '', styles: { halign: 'center' } }, `${t('pdf.code')}: ${kode}\n${t('pdf.dateSaved')}: ${tanggalSimpan}`],
        ],
        theme: 'grid',
        headStyles: { fillColor: [22, 160, 133] },
        bodyStyles: { valign: 'middle' },
        columnStyles: {
          0: { cellWidth: 30, halign: 'center' }, // Column for the logo
          1: { cellWidth: 150, halign: 'left' }, // Column for patient info
        },
        didDrawCell: (data) => {
          // Insert logo in the first column of the first row
          if (data.row.index === 0 && data.column.index === 0) {
            const imgWidth = 18;
            const imgHeight = 18;
            const padding = 2; // Add some padding to center the image
            const imgX = data.cell.x + (data.cell.width - imgWidth) / 2;
            const imgY = data.cell.y + (data.cell.height - imgHeight) / 2;
    
            // Add the logo image to the cell
            doc.addImage(logoData, 'PNG', imgX, imgY, imgWidth, imgHeight);
          }
        },
      });
      // Membuat tabel untuk hasil analisis
      doc.setFontSize(12);
      doc.text(t('pdf.analysisTitle'), 15, 50);
    
      // Tabel data
      doc.autoTable({
        startY: 52,
        head: [[t('pdf.observation'), t('pdf.value')]],
        body: [
          [t('pdf.c2Depth'), analysisResults.AngleC2 !== null ? analysisResults.AngleC2 : 'N/A'],
          [t('pdf.c2InferiorBorder'), analysisResults.ConcvC2 !== null ? analysisResults.ConcvC2 : 'N/A'],
          [t('pdf.c3Depth'), analysisResults.AngleC3 !== null ? analysisResults.AngleC3 : 'N/A'],
          [t('pdf.c3InferiorBorder'), analysisResults.ConcvC3 !== null ? analysisResults.ConcvC3 : 'N/A'],
          [t('pdf.c3BAR'), analysisResults.BARC3 !== null ? analysisResults.BARC3 : 'N/A'],
          [t('pdf.c3PAR'), analysisResults.PARC3 !== null ? analysisResults.PARC3 : 'N/A'],
          [t('pdf.c3Morphology'), analysisResults.MorfologiC3 !== null ? analysisResults.MorfologiC3 : 'N/A'],
          [t('pdf.c4Depth'), analysisResults.AngleC4 !== null ? analysisResults.AngleC4 : 'N/A'],
          [t('pdf.c4InferiorBorder'), analysisResults.ConcvC4 !== null ? analysisResults.ConcvC4 : 'N/A'],
          [t('pdf.c4BAR'), analysisResults.BARC4 !== null ? analysisResults.BARC4 : 'N/A'],
          [t('pdf.c4PAR'), analysisResults.PARC4 !== null ? analysisResults.PARC4 : 'N/A'],
          [t('pdf.c4Morphology'), analysisResults.MorfologiC4 !== null ? analysisResults.MorfologiC4 : 'N/A'],
          [t('pdf.csScore'), analysisResults.CS !== null ? analysisResults.CS : 'N/A'],
          [t('pdf.csDesc'), analysisResults.CSDescription !== null ? analysisResults.CSDescription : 'N/A'],
        ],
        theme: 'grid',
        headStyles: { fillColor: [22, 160, 133] },
        alternateRowStyles: { fillColor: [245, 245, 245] },
      });
    
      // Menambahkan gambar hasil analisis ke PDF
      const imgWidth = 130; // Ukuran gambar dalam mm
      const imgHeight = (canvasImage.height / canvasImage.width) * imgWidth; // Pertahankan rasio
      doc.addImage(imgData, 'PNG', 10, doc.autoTable.previous.finalY + 10, imgWidth, imgHeight);
    
      // Simpan PDF
      doc.save(`Rekaman_Medis_${kode}.pdf`);
    };

   
    // Tambahkan fungsi untuk menggeser gambar dengan tombol
    const moveImage = (direction) => {
      const step = 10; // jumlah pixel per geser
      if (direction === "up") offsetYRef.current -= step;
      if (direction === "down") offsetYRef.current += step;
      if (direction === "left") offsetXRef.current -= step;
      if (direction === "right") offsetXRef.current += step;

      // Update state offset agar sinkron
      setOffsetX(offsetXRef.current);
      setOffsetY(offsetYRef.current);

      // Gambar ulang
      if (radiographImage) {
        drawImageWithZoomAndDrawing(radiographImage, zoomLevel);
      }
    };
   
  return (
    <>
          {/* Modal Popup */}
          <Modal isOpen={isOpen} onClose={onClose}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>{t('analysis.modalTitle')}</ModalHeader>
            <ModalCloseButton />
            <ModalBody>
        <Grid templateColumns="150px 1fr" gap={2}>
          <Text as='b'>{t('analysis.cervical2')}</Text>
          <Text></Text>
          <Text>{t('analysis.depthC2')}</Text>
          <Text>{analysisResults.AngleC2 !== null ? analysisResults.AngleC2 : 'N/A'}</Text>
          <Text>{t('analysis.concavityC2')}</Text>
          <Text>{analysisResults.ConcvC2 !== null ? analysisResults.ConcvC2 : 'N/A'}</Text>

          <Text></Text>
          <Text></Text>
          <Text as='b'>{t('analysis.cervical3')}</Text>
          <Text></Text>
          <Text>{t('analysis.depthC3')}</Text>
          <Text>{analysisResults.AngleC3 !== null ? analysisResults.AngleC3 : 'N/A'}</Text>
          <Text>{t('analysis.concavityC3')}</Text>
          <Text>{analysisResults.ConcvC3 !== null ? analysisResults.ConcvC3 : 'N/A'}</Text>
          <Text>{t('analysis.barC3')}</Text>
          <Text>{analysisResults.BARC3 !== null ? analysisResults.BARC3 : 'N/A'}</Text>
          <Text>{t('analysis.parC3')}</Text>
          <Text>{analysisResults.PARC3 !== null ? analysisResults.PARC3 : 'N/A'}</Text>
          <Text>{t('analysis.morphologyC3')}</Text>
          <Text>{analysisResults.MorfologiC3 !== null ? analysisResults.MorfologiC3 : 'N/A'}</Text>

          <Text></Text>
          <Text></Text>
          <Text as='b'>{t('analysis.cervical4')}</Text>
          <Text></Text>
          <Text>{t('analysis.depthC4')}</Text>
          <Text>{analysisResults.AngleC4 !== null ? analysisResults.AngleC4 : 'N/A'}</Text>
          <Text>{t('analysis.concavityC4')}</Text>
          <Text>{analysisResults.ConcvC4 !== null ? analysisResults.ConcvC4 : 'N/A'}</Text>
          <Text>{t('analysis.barC4')}</Text>
          <Text>{analysisResults.BARC4 !== null ? analysisResults.BARC4 : 'N/A'}</Text>
          <Text>{t('analysis.parC4')}</Text>
          <Text>{analysisResults.PARC4 !== null ? analysisResults.PARC4 : 'N/A'}</Text>
          <Text>{t('analysis.morphologyC4')}</Text>
          <Text>{analysisResults.MorfologiC4 !== null ? analysisResults.MorfologiC4 : 'N/A'}</Text>

          <Text></Text>
          <Text></Text>
          <Text as='b'>{t('analysis.csValue')}</Text>
          <Text as='b'>{analysisResults.CS !== null ? analysisResults.CS : 'N/A'}</Text>
          <Text as='b'>{t('analysis.csDescription')}</Text>
          <Text as='b'>{analysisResults.CSDescription !== null ? analysisResults.CSDescription : 'N/A'}</Text>
        </Grid>
          </ModalBody>
            <ModalFooter>
              <Button colorScheme="blue" onClick={onClose}>
                {t('analysis.close')}
              </Button>
              <Button colorScheme="green" onClick={simpanData}>
                {t('analysis.saveData')}
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>

        <Modal isOpen={isTutorialOpen} onClose={onTutorialClose} size='xl' isCentered>
          <ModalOverlay />
          <ModalContent maxW={{ base: "95%", md: "85%", lg: "1100px" }}>
            <ModalHeader>{t('analysis.tutorialModalTitle')}</ModalHeader>
            <ModalCloseButton />
            <ModalBody pb='24px'>
              <Box>
                <video
                  controls
                  muted
                  style={{ width: '100%', borderRadius: '8px' }}
                >
                  <source src='/video/TCVMS.mp4' type='video/mp4' />
                  {t('analysis.browserNoVideo')}
                </video>
                <Text mt='12px' fontSize='sm' color='gray.500' textAlign='center'>
                  {t('analysis.tutorialDescription')}
                </Text>
              </Box>
            </ModalBody>
          </ModalContent>
        </Modal>

        <Card {...rest} mb='20px' align='center' p='20px'>
          <Flex direction='column' w='100%'>
            
            {/* Canvas */}
            <Box ref={containerRef} w='100%' mb='20px'>
              <canvas
                ref={canvasRef}
                width={canvasDimensions.width}
                height={canvasDimensions.height}
                style={{ border: '1px solid #E2E8F0', borderRadius: '8px', width: '100%', height: 'auto' }}
                onClick={handleCanvasClick}
              ></canvas>
            </Box>

            {/* Panel kontrol */}
            <Flex
              direction={{ base: "column", md: "row" }}
              justify='space-between'
              align={{ base: "stretch", md: "flex-end" }}
              gap='20px'
              w='100%'
              flexWrap='wrap'
            >
              {/* Grup: Zoom & History */}
              <Flex direction='column' gap='8px'>
                <Text fontSize='xs' fontWeight='600' color='gray.500' textTransform='uppercase'>
                  {t('analysis.display')}
                </Text>
                <Flex gap='8px'>
                  <Button onClick={handleUndo} w='60px' h='48px' variant='outline' fontWeight='500'>
                    {t('analysis.undo')}
                  </Button>
                  <Button onClick={handleRedo} w='60px' h='48px' variant='outline' fontWeight='500'>
                    {t('analysis.redo')}
                  </Button>
                </Flex>
              </Flex>

              {/* Grup: Aksi Utama */}
              <Flex direction='column' gap='8px'>
                <Text fontSize='xs' fontWeight='600' color='gray.500' textTransform='uppercase'>
                  {t('analysis.actions')}
                </Text>
                <Flex gap='12px' flexWrap='wrap'>
                  <Button
                    w='150px'
                    h='48px'
                    variant='brand'
                    fontWeight='600'
                    onClick={handleOpenButtonClick}
                  >
                    {t('analysis.openImage')}
                  </Button>
                  <Button
                    w='150px'
                    h='48px'
                    colorScheme='green'
                    fontWeight='600'
                    onClick={analisis}
                  >
                    {t('analysis.analyze')}
                  </Button>
                  <Button
                    w='150px'
                    h='48px'
                    colorScheme='orange'
                    fontWeight='600'
                    onClick={onTutorialOpen}
                  >
                    {t('analysis.tutorial')}
                  </Button>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </Card>
    </>
  );
}